import { reactive, readonly } from 'vue'
import axios from '@/lib/API/client'
import { isLikelyNetworkError } from '@/lib/API/network-error'

import { belongsToSignedInUser, onePassAtATime, signedInUserId, toIsoDate } from '@/lib/helpers/send-queue'
import { hasValue } from '@/lib/helpers/numeric'
import { sleep } from '@/lib/helpers/time'
import {
    buildPolarCaptureKey,
    findRelatedPolarCapture,
    getPolarCaptureById,
    getPolarCaptureByKey,
    isPolarCaptureLinkedByFormerVersion,
    isPolarCaptureSent,
    listPolarCaptures,
    markPolarCaptureDeletedFromPolar,
    markPolarCaptureUploaded,
    normalizePolarTimestampMs,
    POLAR_CAPTURE_SOURCE_LIVE_BLE,
    POLAR_CAPTURE_SOURCE_MEMORY,
    savePolarMemoryCapture,
} from '@/lib/sqlite'
import { appendUploadSourceDevice } from '@/lib/helpers/upload-source'

// The captures the send pass looks at: the local list keeps 90 days.
const POLAR_CAPTURE_SEND_LIMIT = 200

const getCaptureTimestamp = (capture) =>
    Number(capture?.startedAt || capture?.polarExerciseTs || capture?.createdAt || 0) || 0

export const runPolarPsftpActionWithRetry = async (action) => {
    let lastError = null

    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const status = await action()
            if (status) {
                return status
            }
        } catch (error) {
            lastError = error
        }

        if (attempt === 0) {
            await sleep(100)
        }
    }

    if (lastError) {
        throw lastError
    }

    return false
}

export const getCurrentPolarExerciseInfo = async (heartrate) => {
    const polarExerciseTs = normalizePolarTimestampMs(heartrate?.PSFTP?.value?.properties?.exercise)
    if (!Number.isFinite(polarExerciseTs) || polarExerciseTs <= 0) {
        return { polarExerciseTs: null, captureKey: null, capture: null }
    }

    const deviceId = heartrate?.lastConnectedId?.value || null
    const captureKey = buildPolarCaptureKey({
        source: POLAR_CAPTURE_SOURCE_MEMORY,
        deviceId,
        polarExerciseTs,
    })
    const capture = await getPolarCaptureByKey(captureKey)

    return {
        polarExerciseTs,
        captureKey,
        capture,
        deviceId,
    }
}

export const stopPolarRecordingIfNeeded = async (heartrate) => {
    if (!heartrate?.isRecording?.value) {
        return true
    }

    return runPolarPsftpActionWithRetry(() => heartrate?.PSFTP?.value?.stopRecording())
}

export const importCurrentPolarExerciseToApp = async ({
    heartrate,
    recordingIntentId = undefined,
    startedAt = null,
    endedAt = null,
} = {}) => {
    const currentExercise = await getCurrentPolarExerciseInfo(heartrate)
    if (!currentExercise?.polarExerciseTs || !currentExercise.captureKey) {
        throw new Error('polar-import-missing')
    }

    const arrData = await heartrate?.getExercise?.()
    if (!Array.isArray(arrData) || arrData.length === 0) {
        throw new Error('polar-import-missing')
    }

    let samples = arrData
        .map((item) => [Number(item?.time), Number(item?.value)])
        .filter((sample) => Number.isFinite(sample[0]) && Number.isFinite(sample[1]))

    if (!samples.length) {
        throw new Error('polar-import-missing')
    }

    const resolvedEndedAt = normalizePolarTimestampMs(endedAt) || currentExercise.polarExerciseTs
    const resolvedStartedAt =
        normalizePolarTimestampMs(startedAt) ||
        resolvedEndedAt - Math.max(0, (Number(samples[samples.length - 1]?.[0]) || 0) * 1000)

    if (resolvedStartedAt && resolvedEndedAt && resolvedEndedAt > resolvedStartedAt) {
        const expectedDurationSec = Math.max(1, Math.round((resolvedEndedAt - resolvedStartedAt) / 1000))
        if (samples.length > expectedDurationSec) {
            samples = samples.slice(0, expectedDurationSec)
        }
    }

    const saved = await savePolarMemoryCapture({
        deviceId: currentExercise.deviceId,
        deviceName: heartrate?.properties?.name,
        polarExerciseTs: currentExercise.polarExerciseTs,
        startedAt: resolvedStartedAt,
        endedAt: resolvedEndedAt,
        recordingIntentId,
        userId: signedInUserId(),
        samples,
    })
    // P7: an imported capture goes to the server at once.
    void sendPolarCaptures()
    return saved
}

export const stopAndImportCurrentPolarExercise = async ({
    heartrate,
    recordingIntentId = undefined,
    startedAt = null,
    endedAt = null,
} = {}) => {
    const stopped = await stopPolarRecordingIfNeeded(heartrate)
    if (!stopped) {
        throw new Error('polar-stop-failed')
    }

    return importCurrentPolarExerciseToApp({
        heartrate,
        recordingIntentId,
        startedAt,
        endedAt,
    })
}

export const clearCurrentPolarExerciseFromDevice = async ({ heartrate, capture = null } = {}) => {
    const currentExercise = await getCurrentPolarExerciseInfo(heartrate)
    const captureToUpdate = capture || currentExercise.capture
    const status = await runPolarPsftpActionWithRetry(() => heartrate?.PSFTP?.value?.removeExercise())

    if (!status) {
        return false
    }

    if (captureToUpdate?.id) {
        try {
            await markPolarCaptureDeletedFromPolar(captureToUpdate.id)
        } catch (error) {
            console.warn('[Polar] Failed to mark local capture as cleared from device', error)
        }
    }

    return true
}

export const buildPolarHeartrateCsv = (samples = []) => {
    const rows = Array.isArray(samples)
        ? samples
              .map((sample) => [Number(sample?.[0]), Number(sample?.[1])])
              .filter((sample) => Number.isFinite(sample[0]) && Number.isFinite(sample[1]))
        : []

    if (!rows.length) {
        return null
    }

    return ['time;bpm', ...rows.map(([time, bpm]) => `${time};${bpm}`)].join('\n')
}

export const getPolarCaptureUploadFilename = (capture = {}) => {
    const captureDate = Number(capture?.polarExerciseTs || capture?.endedAt || capture?.startedAt || Date.now())
    const normalizedCaptureDate = Number.isFinite(captureDate) && captureDate > 0 ? captureDate : Date.now()
    const sourceSuffix = capture?.source === POLAR_CAPTURE_SOURCE_LIVE_BLE ? 'live-ble' : 'memory'
    return `polar-${sourceSuffix}-${normalizedCaptureDate}.csv`
}

// P7: a capture goes to the server on its own, without a session: the server
// joins it to the session of its launch or phone recording (recording_uuid),
// else to the only one it overlaps, else keeps it waiting for the rider to
// choose it. True once the server has it (sent now, or already: 409).
export const uploadPolarCapture = async (capture) => {
    const csvContent = buildPolarHeartrateCsv(capture?.samples)
    if (!csvContent) return false

    const filename = getPolarCaptureUploadFilename(capture)
    const fields = {
        sensor_type: 'heartrate',
        light: '1',
        name: filename,
        date: Math.floor(Number(capture.polarExerciseTs || capture.endedAt || capture.startedAt || Date.now()) / 1000),
        start_at: toIsoDate(capture.startedAt),
        end_at: toIsoDate(capture.endedAt || capture.polarExerciseTs),
        serial_id: capture.deviceId,
        description: capture.deviceName,
        recording_uuid: capture.recordingIntentId,
    }

    const formData = new FormData()
    formData.append('source', new Blob([csvContent], { type: 'text/csv;charset=utf-8' }), filename)
    for (const [field, value] of Object.entries(fields)) {
        if (hasValue(value)) formData.append(field, String(value))
    }
    await appendUploadSourceDevice(formData)

    try {
        await axios.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        return true
    } catch (error) {
        if (error?.response?.status === 409) return true
        throw error
    }
}

export const findPrimaryPolarCapture = (capture, captures = []) => {
    if (!capture) return null
    if (capture?.source === POLAR_CAPTURE_SOURCE_MEMORY) {
        return capture
    }

    const relatedCapture = findRelatedPolarCapture(capture, captures)
    if (relatedCapture?.source === POLAR_CAPTURE_SOURCE_MEMORY) {
        return relatedCapture
    }

    return capture
}

// One ride, one group: its Polar memory capture (the one used) and the live
// Bluetooth copy kept in case the memory import fails. Newest ride first.
export const groupPolarCaptures = (captures = []) => {
    const normalizedCaptures = Array.isArray(captures) ? captures : []
    const seenCaptureIds = new Set()
    const groups = []

    for (const capture of normalizedCaptures) {
        if (!capture?.id || seenCaptureIds.has(capture.id)) {
            continue
        }

        const primaryCapture = findPrimaryPolarCapture(capture, normalizedCaptures) || capture
        const relatedCapture = findRelatedPolarCapture(primaryCapture, normalizedCaptures)
        const groupCaptures = [primaryCapture]

        if (relatedCapture?.id && relatedCapture.id !== primaryCapture.id) {
            groupCaptures.push(relatedCapture)
        }

        for (const item of groupCaptures) {
            if (item?.id) {
                seenCaptureIds.add(item.id)
            }
        }

        groups.push({
            id: String(primaryCapture.id),
            primaryCapture,
            memoryCapture: groupCaptures.find((item) => item?.source === POLAR_CAPTURE_SOURCE_MEMORY) || null,
            liveCapture: groupCaptures.find((item) => item?.source === POLAR_CAPTURE_SOURCE_LIVE_BLE) || null,
            captures: groupCaptures,
        })
    }

    return groups.sort(
        (left, right) => getCaptureTimestamp(right.primaryCapture) - getCaptureTimestamp(left.primaryCapture)
    )
}

// A ride the server has: its primary capture was sent, or a former version
// uploaded one of its copies into its session. The other copy of such a ride
// is never sent: it would replace the cardio of that session.
export const isPolarCaptureGroupSent = (group) =>
    isPolarCaptureSent(group?.primaryCapture) || !!group?.captures?.some(isPolarCaptureLinkedByFormerVersion)

// Sends the captures the server does not have yet, one per ride (its primary
// capture), each only under the account it was made under (send-queue.js): at
// each import, at each start of the app and when the network comes back. A
// capture recorded before this version is sent once too.
const sendPass = async () => {
    let sent = false

    try {
        const captures = await listPolarCaptures({ limit: POLAR_CAPTURE_SEND_LIMIT })
        for (const group of groupPolarCaptures(captures)) {
            const { primaryCapture } = group
            if (isPolarCaptureGroupSent(group) || !belongsToSignedInUser(primaryCapture.userId)) continue

            try {
                const capture = await getPolarCaptureById(primaryCapture.id, { withSamples: true })
                if (!(await uploadPolarCapture(capture))) continue
            } catch (error) {
                // The network is missing: the next pass sends it.
                if (isLikelyNetworkError(error)) return
                // A refusal, or a capture the phone cannot read: kept on the
                // phone and offered again next time, without holding back the others.
                console.warn('[Polar] Capture not sent', primaryCapture.id, error?.response?.status ?? error)
                continue
            }

            await markPolarCaptureUploaded(primaryCapture.id)
            sent = true
        }
    } finally {
        if (sent) notifyPolarCapturesChanged()
    }
}

export const sendPolarCaptures = onePassAtATime(sendPass)

const createPendingExercisePromptInitialState = () => ({
    isOpen: false,
    context: 'connect',
    captureKey: null,
    deleteAfterSave: true,
})

const pendingExercisePromptState = reactive(createPendingExercisePromptInitialState())
const capturesInboxState = reactive({ revision: 0 })

let pendingExercisePromptPromise = null
let resolvePendingExercisePromptPromise = null
let lastAutoHandledPendingExerciseCaptureKey = null

const resetPendingExercisePromptState = () => {
    Object.assign(pendingExercisePromptState, createPendingExercisePromptInitialState())
}

export const polarPendingExercisePromptState = readonly(pendingExercisePromptState)
export const polarCapturesInboxState = readonly(capturesInboxState)

export const notifyPolarCapturesChanged = () => {
    capturesInboxState.revision += 1
}

export const requestPolarPendingExercisePrompt = ({ context = 'connect', captureKey = null } = {}) => {
    if (pendingExercisePromptPromise) {
        return pendingExercisePromptPromise
    }

    pendingExercisePromptState.isOpen = true
    pendingExercisePromptState.context = context
    pendingExercisePromptState.captureKey = captureKey || null
    pendingExercisePromptState.deleteAfterSave = true

    pendingExercisePromptPromise = new Promise((resolve) => {
        resolvePendingExercisePromptPromise = resolve
    })

    return pendingExercisePromptPromise
}

export const resolvePolarPendingExercisePrompt = ({
    action = 'cancel',
    deleteAfterSave = pendingExercisePromptState.deleteAfterSave,
} = {}) => {
    const resolve = resolvePendingExercisePromptPromise
    const result = {
        action,
        deleteAfterSave: !!deleteAfterSave,
    }

    pendingExercisePromptPromise = null
    resolvePendingExercisePromptPromise = null
    resetPendingExercisePromptState()

    resolve?.(result)
    return result
}

export const setPolarPendingExerciseDeleteAfterSave = (value) => {
    pendingExercisePromptState.deleteAfterSave = !!value
}

export const shouldAutoPromptPolarPendingExercise = (captureKey) =>
    !!captureKey && captureKey !== lastAutoHandledPendingExerciseCaptureKey

export const markPolarPendingExerciseAutoHandled = (captureKey) => {
    lastAutoHandledPendingExerciseCaptureKey = captureKey || null
}

export const resetPolarPendingExerciseAutoHandled = (captureKey = null) => {
    if (!captureKey || lastAutoHandledPendingExerciseCaptureKey === captureKey) {
        lastAutoHandledPendingExerciseCaptureKey = null
    }
}

const recoveryState = reactive({ pending: null })

const normalizeRecoverySnapshot = (snapshot = {}) => {
    if (!snapshot?.usePolar) {
        return null
    }

    return {
        recordingIntentId: snapshot.intentId ?? undefined,
        startedAt: snapshot.startedAt ?? null,
        endedAt: snapshot.endedAt ?? null,
        sensorSerialId: snapshot.sensorSerialId ?? null,
        sensorName: snapshot.sensorName ?? null,
        polarDeviceId: snapshot.polarDeviceId ?? null,
        polarDeviceName: snapshot.polarDeviceName ?? null,
        polarExpectedExerciseTs: snapshot.polarExpectedExerciseTs ?? null,
        polarMemoryExerciseTs: snapshot.polarMemoryExerciseTs ?? null,
    }
}

export const polarRecoveryState = readonly(recoveryState)

export const queuePolarRecoverySnapshot = (snapshot = {}) => {
    const pending = normalizeRecoverySnapshot(snapshot)
    if (!pending) {
        return null
    }

    recoveryState.pending = pending
    return pending
}

export const clearPolarRecoverySnapshot = () => {
    recoveryState.pending = null
}

export const isPolarRecoveryCompatibleWithCurrentHeartrate = (heartrate) => {
    if (!recoveryState.pending || !heartrate?.isConnected?.value || !heartrate?.isPolar?.value) {
        return false
    }

    const expectedDeviceId = recoveryState.pending.polarDeviceId || null
    const currentDeviceId = heartrate?.lastConnectedId?.value || null
    const expectedExerciseTs =
        normalizePolarTimestampMs(recoveryState.pending.polarMemoryExerciseTs) ||
        normalizePolarTimestampMs(recoveryState.pending.polarExpectedExerciseTs)
    const currentExerciseTs = normalizePolarTimestampMs(heartrate?.PSFTP?.value?.properties?.exercise)

    if (expectedDeviceId && currentDeviceId && expectedDeviceId !== currentDeviceId) {
        return false
    }

    if (expectedExerciseTs && currentExerciseTs && expectedExerciseTs !== currentExerciseTs) {
        return false
    }

    return true
}

export const shouldOpenPolarRecoveryForCurrentHeartrate = (heartrate) => {
    if (!recoveryState.pending) {
        return false
    }

    if (!isPolarRecoveryCompatibleWithCurrentHeartrate(heartrate)) {
        return false
    }
    return !!heartrate?.isRecording?.value || !!heartrate?.isReadyToFetchRecord?.value
}
